import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Nurse } from 'npx-family-happy-common';
import { map } from 'rxjs';
import { CaregiverCreationBody } from 'src/app/shared/models/caregiver-creation-body.model';
import { environment } from 'src/environments/environment';

const DEFAULT_FILTERS = {
  age_lower: null,
  age_upper: null,
  cats: false,
  cluster1: false,
  cluster2: false,
  cluster3: false,
  contract: null,
  days: null,
  disability: false,
  doc_ref: null,
  dogs: false,
  dogs1: false,
  dogs2: false,
  dogs3: false,
  dogs4: false,
  drugs: false,
  email: null,
  fhsub: false,
  homeService: false,
  hosting: false,
  hours: null,
  illness: false,
  limit: 10,
  name: null,
  nightShift: false,
  page: 0,
  pension: false,
  phone: null,
  province: null,
  walking: false
}

@Injectable({
  providedIn: 'root'
})
export class NurseService {
  baseURL = `${environment.baseURL}/v1/nurses`;

  constructor(private http: HttpClient) { }

  getSingleNurse(id: string) {
    const filters = {
      ...DEFAULT_FILTERS,
      doc_ref: id,
      limit: 1
    };

    return this.http.post<Nurse[]>(this.baseURL + '/find', filters);
  }

  createNurseFromPayment(body: CaregiverCreationBody) {
    return this.http.post<Nurse>(this.baseURL + '/import-from-payment', body);
  }

  createNurse(requestData: Nurse) {
    return this.http.post(this.baseURL + '/import', requestData);
  }

  updateNurseFromForm(nurse: Nurse) {
    return this.http.post(this.baseURL + '/import-form', nurse);
  }

  findNurseByEmail(email: string) {
    let request = {
      age_lower: null,
      age_upper: null,
      car: false,
      cleaning: false,
      cluster1: false,
      cluster2: false,
      cluster3: false,
      cluster4: false,
      contract: null,
      cooking: false,
      cv: false,
      days: null,
      disostruzione: false,
      doc_ref: null,
      email: email,
      english: false,
      fhsub: false,
      french: false,
      german: false,
      greenpass: false,
      homework: false,
      hours: null,
      italian: false,
      licence: false,
      limit: 1,
      minimum_evaluation: false,
      name: null,
      not_selected: false,
      not_smoker: false,
      one_ref: false,
      page: 1,
      phone: null,
      province: null,
      spanish: false,
      taxi: false
    };

    return this.http.post<Nurse[]>(this.baseURL + '/find', request);
  }

  /* Utilities methods */

  // It converts an object to a string. e.g.: {sun: true, mon: false, tue: true} -> 'sun-tue'
  fromAvailObjectToString(object: any, type: string) {
    let result = '';
    if (object) {
      switch (type) {
        case 'hours':
          result += object['mor'] ? 'mor' : '';
          result += object['aft'] ? (result !== '' ? '-aft' : 'aft') : '';
          result += object['eve'] ? (result !== '' ? '-eve' : 'eve') : '';
          return result;
        case 'days':
          result += object['sun'] ? 'sun' : '';
          result += object['mon'] ? (result !== '' ? '-mon' : 'mon') : '';
          result += object['tue'] ? (result !== '' ? '-tue' : 'tue') : '';
          result += object['wed'] ? (result !== '' ? '-wed' : 'wed') : '';
          result += object['thu'] ? (result !== '' ? '-thu' : 'thu') : '';
          result += object['fri'] ? (result !== '' ? '-fri' : 'fri') : '';
          result += object['sat'] ? (result !== '' ? '-sat' : 'sat') : '';
          return result;
        case 'contract':
          result += object['occ'] ? 'occ' : '';
          result += object['part'] ? (result !== '' ? '-part' : 'part') : '';
          result += object['full'] ? (result !== '' ? '-full' : 'full') : '';
          return result;
        case 'tasks1':
        case 'tasks2':
          result += object['assistance'] ? 'assistance' : '';
          result += object['care'] ? (result !== '' ? '-care' : 'care') : '';
          result += object['drugs'] ? (result !== '' ? '-drugs' : 'drugs') : '';
          result += object['cooking'] ? (result !== '' ? '-cooking' : 'cooking') : '';
          result += object['housing'] ? (result !== '' ? '-housing' : 'housing') : '';
          result += object['other'] ? (result !== '' ? '-other' : 'other') : '';
          return result;
        default:
          return '';
      }
    } else {
      return result;
    }
  }

  // It converts a string to an object (hour, contract or days property of Nurse). e.g.: 'sun-tue' -> {sun: true, mon: false, tue: true}
  fromStringToAvailObject(origin: string, type: string) {
    if (origin) {
      let keys = origin.split('-') || [];
      switch (type) {
        case 'hours':
          return {
            mor: keys.findIndex(item => item === 'mor') !== -1,
            aft: keys.findIndex(item => item === 'aft') !== -1,
            eve: keys.findIndex(item => item === 'eve') !== -1,
          }
        case 'days':
          return {
            sun: keys.findIndex(item => item === 'sun') !== -1,
            mon: keys.findIndex(item => item === 'mon') !== -1,
            tue: keys.findIndex(item => item === 'tue') !== -1,
            wed: keys.findIndex(item => item === 'wed') !== -1,
            thu: keys.findIndex(item => item === 'thu') !== -1,
            fri: keys.findIndex(item => item === 'fri') !== -1,
            sat: keys.findIndex(item => item === 'sat') !== -1,
          };
        case 'contract':
          return {
            occ: keys.findIndex(item => item === 'occ') !== -1,
            part: keys.findIndex(item => item === 'part') !== -1,
            full: keys.findIndex(item => item === 'full') !== -1,
          };
        case 'tasks1':
        case 'tasks2':
          return {
            assistance: keys.findIndex(item => item === 'assistance') !== -1,
            care: keys.findIndex(item => item === 'care') !== -1,
            drugs: keys.findIndex(item => item === 'drugs') !== -1,
            cooking: keys.findIndex(item => item === 'cooking') !== -1,
            housing: keys.findIndex(item => item === 'housing') !== -1,
            other: keys.findIndex(item => item === 'other') !== -1
          }
        default:
          return {
            occ: false,
            part: false,
            full: false,
          };
      }
    } else {
      switch (type) {
        case 'hours':
          return {
            mor: false,
            aft: false,
            eve: false,
          }
        case 'days':
          return {
            sun: false,
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
          };
        case 'contract':
          return {
            occ: false,
            part: false,
            full: false,
          };
        case 'tasks1':
        case 'tasks2':
          return {
            assistance: false,
            care: false,
            drugs: false,
            cooking: false,
            housing: false,
            other: false
          }
        default:
          return undefined;
      }
    }
  }

  increaseApplicationsCounter(docRef: string) {
    return this.getSingleNurse(docRef).pipe(map((nurses => {
      let nurse = nurses[0];
      nurse.engagement.applications = nurse.engagement.applications + 1;
      return this.updateNurseFromForm(nurse);
    })));
  }
}
